














































import { Ref, Prop, Component, Vue } from 'vue-property-decorator'
import { rgbToHex } from '@/helpers/color'

@Component
export default class InputColor extends Vue {
    @Prop({ type: Boolean, default: false }) readonly hideHexCodeLabel!: boolean
    @Prop() readonly label!: string
    @Prop() readonly value!: string
    hueValue: string = null
    offsetX: number = 0
    offsetY: number = 0

    @Ref() paletteParent!: HTMLDivElement
    @Ref() paletteCanvas!: HTMLCanvasElement
    @Ref() paletteSelectorCanvas!: HTMLCanvasElement
    paletteContext: CanvasRenderingContext2D = null
    paletteSelectorContext: CanvasRenderingContext2D = null

    @Ref() hueParent!: HTMLDivElement
    @Ref() hueCanvas!: HTMLCanvasElement
    @Ref() hueSelectorCanvas!: HTMLCanvasElement
    hueContext: CanvasRenderingContext2D = null
    hueSelectorContext: CanvasRenderingContext2D = null

    showPicker: boolean = false

    mounted(): void {
        this.hueValue = this.value
        this.bindContext()
        this.updateLayout()
        this.repaintPalette()
        this.repaintHue()
    }

    /**
     * Bind canvas contexts to component.
     */
    bindContext(): void {
        this.paletteContext = this.paletteCanvas.getContext('2d')
        this.paletteSelectorContext = this.paletteSelectorCanvas.getContext('2d')
        this.hueContext = this.hueCanvas.getContext('2d')
        this.hueSelectorContext = this.hueSelectorCanvas.getContext('2d')
    }

    /**
     * Update canvas + parent sizes.
     */
    updateLayout(): void {
        this.paletteCanvas.height = this.paletteCanvas.clientHeight
        this.paletteCanvas.width = this.paletteCanvas.clientWidth
        this.paletteSelectorCanvas.height = this.paletteCanvas.clientHeight
        this.paletteSelectorCanvas.width = this.paletteCanvas.clientWidth
        this.paletteParent.style.height = `${this.paletteCanvas.height}px`
        this.paletteParent.style.width = `${this.paletteCanvas.width}px`

        this.hueCanvas.height = this.hueCanvas.clientHeight
        this.hueCanvas.width = this.hueCanvas.clientWidth
        this.hueSelectorCanvas.height = this.hueCanvas.clientHeight
        this.hueSelectorCanvas.width = this.hueCanvas.clientWidth
        this.hueParent.style.height = `${this.hueCanvas.height}px`
        this.hueParent.style.width = `${this.hueCanvas.width}px`
    }

    /**
     * When clicking or dragging on the palette, update result value.
     */
    onPaletteInput(event: MouseEvent): void {
        if (event.buttons !== 1) {
            return
        }

        this.offsetX = event.offsetX
        this.offsetY = event.offsetY

        const [r, g, b] = this.paletteContext.getImageData(this.offsetX, this.offsetY, 1, 1).data
        const hexValue = rgbToHex(r, g, b)

        this.updatePaletteSelector(hexValue)
        this.onInput(hexValue)
    }

    /**
     * Update the little circle on the palette canvas.
     * NOTE: We render this on an overlaying canvas so it doesn't interfere with grabbing the image data.
     */
    updatePaletteSelector(color: string): void {
        this.paletteSelectorContext.fillStyle = color
        this.paletteSelectorContext.strokeStyle = '#fff'

        this.paletteSelectorContext.clearRect(0, 0, this.paletteSelectorCanvas.width, this.paletteSelectorCanvas.height)
        this.paletteSelectorContext.beginPath()
        this.paletteSelectorContext.arc(this.offsetX, this.offsetY, 8, 0, 2 * Math.PI)
        this.paletteSelectorContext.fill()
        this.paletteSelectorContext.stroke()
        this.paletteSelectorContext.closePath()
    }

    /**
     * Clear the selector canvas so that the selector dissapears.
     */
    clearPaletteSelector(): void {
        this.paletteSelectorContext.clearRect(0, 0, this.paletteSelectorCanvas.width, this.paletteSelectorCanvas.height)
    }

    /**
     * We draw two gradients, one vertical and one horizontal. These two gradients combined
     * ensure we can select across the entire spectrum.
     */
    repaintPalette(): void {
        const gradient1 = this.paletteContext.createLinearGradient(0, 0, this.paletteCanvas.width, 0)
        gradient1.addColorStop(0, 'white')
        gradient1.addColorStop(1, this.hueValue ?? '#FF0000')
        this.paletteContext.fillStyle = gradient1
        this.paletteContext.fillRect(0, 0, this.paletteCanvas.width, this.paletteCanvas.height)

        const gradient2 = this.paletteContext.createLinearGradient(0, this.paletteCanvas.height, 0, 0)
        gradient2.addColorStop(0, 'black')
        gradient2.addColorStop(1, 'transparent')
        this.paletteContext.fillStyle = gradient2
        this.paletteContext.fillRect(0, 0, this.paletteCanvas.width, this.paletteCanvas.height)
    }

    /**
     * When clicking or dragging on the hue, update result value.
     */
    onHueInput(event: MouseEvent): void {
        if (event.buttons !== 1) {
            return
        }

        const { offsetX, offsetY } = event
        const [r, g, b] = this.hueContext.getImageData(offsetX, offsetY, 1, 1).data
        this.hueValue = rgbToHex(r, g, b)
        this.repaintPalette()
        this.clearPaletteSelector()
    }

    /**
     * Create a rainbow! ┏(-_-)┛ ┗(-_-﻿ )┓ ┗(-_-)┛ ┏(-_-)┓
     */
    repaintHue(): void {
        const offset = (100 / 6) / 100
        const gradient = this.hueContext.createLinearGradient(0, 0, this.hueCanvas.width, 0)
        gradient.addColorStop(0, '#f00')
        gradient.addColorStop(offset, '#ff0')
        gradient.addColorStop(offset * 2, '#0f0')
        gradient.addColorStop(offset * 3, '#0ff')
        gradient.addColorStop(offset * 4, '#00f')
        gradient.addColorStop(offset * 5, '#f0f')
        gradient.addColorStop(offset * 6, '#f00')
        this.hueContext.fillStyle = gradient
        this.hueContext.fillRect(0, 0, this.hueCanvas.width, this.hueCanvas.height)
    }

    /**
     * Emit new color value.
     */
    onManualInput(value: string): void {
        if (/^#(?:[0-9a-f]{3}){1,2}$/i.test(value) === false) {
            return
        }

        this.$emit('input', value)
        this.hueValue = value
        this.repaintPalette()
        this.clearPaletteSelector()
    }

    /**
     * Emit new color value.
     */
    onInput(value: string): void {
        this.$emit('input', value)
    }

    get selectionStyle(): object {
        return {
            background: `${this.value}`,
        }
    }

    get pickerClasses(): object {
        return {
            'input-color__picker': true,
            'input-color__picker--show': this.showPicker,
        }
    }

    get showIcon(): string {
        return this.showPicker ? 'icon-collapse' : 'icon-pipet'
    }

    get hexLabel(): null|string {
        return this.hideHexCodeLabel ? null : 'Hex code'
    }
}
